import '../styles/Card.scss';
import React, { useEffect, useState } from 'react';
import FeatureSlider from './FeatureSlider';
import Products from '../model/product';
import PurchaseButton from './PurchaseButton';
import { Box, Button, Grid, Modal } from '@mui/material';
import { useTheme } from '../hooks/ThemeContext';
import CloseIcon from '@mui/icons-material/Close';
import { useCustomContext } from './ContextHook';
import BottomHeader from './BottomHeader';

const PATH_LOGIN = "/login/?redirect_uri=/premium"
type props = {
    products: Products[];
}

const Card = (props: props) => {
    const [selectedProduct, setSelectedProduct] = useState<Products>(props.products[1]);

    const handleOptionClick = (product: Products) => {
        setSelectedProduct(product);
    }
    const hasTag = selectedProduct.tag !== undefined && selectedProduct.tag.length > 0
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const theme = useTheme();
    const { isLogged, user } = useCustomContext();
    const { configs } = useCustomContext()
    const currentEvent = configs?.currentEvent;
    const isTimerEnabled = currentEvent && currentEvent?.theme?.timer?.isEnabled

    const goToLoginFromModal = () => {
        // eslint-disable-next-line no-restricted-globals
        location.href = PATH_LOGIN;
    }

    const updateButtonPosition = (isBottomVisible: boolean) => {
        const stickyButton = document.querySelector('.continue') as HTMLElement;
        if (stickyButton) {
            if (isBottomVisible) {
                stickyButton.classList.remove('fixed');
                stickyButton.classList.add('relative');
            } else {
                stickyButton.classList.remove('relative');
                stickyButton.classList.add('fixed');
            }
        }
    };

    const debounce = (func: Function, delay: number) => {
        let debounceTimer: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => func(...args), delay);
        };
    };

    useEffect(() => {
        // This useEffect will re-render the component when configs or currentEvent changes
    }, [configs, currentEvent]);

    const debouncedUpdateButtonPosition = debounce(updateButtonPosition, 20);

    useEffect(() => {
        const footerGrid = document.querySelector(".footer-grid") as HTMLElement;
        const visibleHeightMax = isTimerEnabled ? 120 : 90
        const visibleHeightMin = isTimerEnabled ? 5 : 20

        if (footerGrid) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (window.matchMedia("(max-width: 600px)").matches) {
                        const visibleHeight = entry.intersectionRect.height;
                        if (visibleHeight > visibleHeightMax) {
                            debouncedUpdateButtonPosition(true, 20);
                        } else if (visibleHeight < visibleHeightMin) {
                            debouncedUpdateButtonPosition(false, 20);
                        }
                    }
                },
                {
                    root: null,
                    threshold: Array.from({ length: 101 }, (_, i) => i / 100),
                }
            );

            observer.observe(footerGrid);
            return () => {
                if (footerGrid) {
                    observer.unobserve(footerGrid);
                }
            };
        }
    });

    const purchaseButton = {
        borderRadius: '20px',
        border: 'none',
        background: theme.config.puchaseButtonBackgroundColor,
        color: 'white',
        fontSize: '16px',
        lineHeight: '24px',
        fontFamily: 'Lato !important',
        fontWeight: '700 !important',
        marginTop: '10px',
        cursor: 'pointer',
        textTransform: 'none',
        padding: '13px 28px',
        width: '100%',
        "&:hover": {
            opacity: .7,
            backgroundColor: '#FF3363'
        }
    }

    const modalBoxStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '335px',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: '30px',
        padding: '50px 30px 20px 30px'
    };

    const numOfMonths = Number(selectedProduct.months)
    const numOfWeeks = Number(selectedProduct.weeks)

    const billingFrequency = () => {
        let frequency = "";
        if (numOfMonths === 12) {
            frequency = "annually";
        } else if (numOfMonths === 1) {
            frequency = "monthly";
        } else if (numOfMonths === 0) {
            if (numOfWeeks === 1) {
                frequency = "weekly";
            } else {
                frequency = `every ${selectedProduct.weeks}`;
            }
        } else {
            frequency = `every ${selectedProduct.months} months`;
        }
        return frequency;
    }

    const saleClassName = currentEvent?.name !== undefined ? currentEvent?.name : ''
    return (
        <div className="card">
            <div
                className={`left background-card-image ${saleClassName}`}>
                <FeatureSlider></FeatureSlider>
                <a href="https://blog.mutual.app/what-is-mutualup/" target="_blank" rel="noreferrer">Learn More about Premium</a>
            </div>
            <div className="right">
                <div className="options">
                    {
                        props.products.map((product: Products, index: number) => {
                            const isLast = index === props.products.length - 1;
                            return (
                                <div
                                    key={product.item}
                                    className={`option ${selectedProduct && selectedProduct.item === product.item ? 'selected' : ''} ${isLast ? 'last-option' : ''}`}
                                    onClick={() => handleOptionClick(product)}
                                >
                                    <div>
                                        <h4 className='option-title'>{product.item}</h4>
                                        {product.tag?.toLowerCase() === "most popular" ? (
                                            <span className='most-popular'>Most Popular</span>
                                        ) : product.tag?.toLowerCase() === "best value" ? (
                                            <span className='best-value'>Best Value</span>
                                        ) : null}

                                        <h6>
                                            {product.price}
                                            {
                                                !isNaN(Number(product.months)) && Number(product.months) > 0 && (product.perWeek !== undefined ? <span>({product.perWeek}/week)</span> : <span>({product.perMonth}/month)</span>)
                                            }
                                        </h6>
                                    </div>
                                    <div>
                                        {product.percentOff !== 0 && <p><span className='save'>Save {product.percentOff}%</span></p>}
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
                <Grid
                    item
                    xs={12}
                    sm={5}
                    container
                    sx={{ padding: '0px !important' }}
                >

                    <Modal
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                    >
                        <Box sx={modalBoxStyle} className='modal-container'>

                            <div className='close-btn' onClick={handleClose}>
                                <CloseIcon />
                            </div>

                            <Grid container className='modal-container'>
                                {hasTag && <p className='modal-best-value'>{selectedProduct.tag}</p>}

                                <Grid container flexDirection='column'>

                                    <h3 className="modal-item-title">
                                        {`${selectedProduct.item} MutualUp Subscription`}
                                    </h3>
                                    <p className="modal-item-price">{selectedProduct.price}
                                        <span>
                                            {selectedProduct.perWeek ? ` (${selectedProduct.perWeek}/week)` : ` (${selectedProduct.perMonth}/mo)`}
                                        </span>
                                    </p>
                                    {
                                        selectedProduct.percentOff > 0 && <p className="modal-percent-off">
                                            Save over <span style={{ color: theme.config.monthlyTextColor }} className="percent-off-price">{selectedProduct.percentOff.toString()}%</span>
                                            &nbsp;
                                            {selectedProduct.discountText}
                                        </p>
                                    }

                                    {selectedProduct.productValue === 'mutual:subscription:renewable:6monthx2'
                                        ? <p className='wingman-text-instructions'> We will send you an email with 2 promo codes after purchase. Both subscriptions will be active for 6 months after the 1st code is activated.</p>
                                        : null
                                    }

                                </Grid>

                                <Grid
                                    container
                                    flexDirection='column'
                                    className='button-container'
                                >

                                    {selectedProduct.productValue === 'mutual:subscription:renewable:6monthx2'
                                        ? <PurchaseButton productValue={selectedProduct.productValue} isSelf={false} isWingman={true} />
                                        :
                                        <>
                                            {
                                                isLogged
                                                    ?
                                                    (user.premium
                                                        ?
                                                        // if user has premium we do not allow him to buy a subscription 
                                                        null
                                                        :
                                                        // if he does not have premium we allow him 
                                                        <PurchaseButton productValue={selectedProduct.productValue} isSelf={true} />
                                                    )
                                                    :
                                                    <Button
                                                        className='modal-button-sign-in'
                                                        onClick={() => goToLoginFromModal()}
                                                    >Sign In
                                                    </Button>
                                            }
                                            <PurchaseButton productValue={selectedProduct.productValue} isSelf={false} />
                                        </>
                                    }

                                    <p className="modal-percent-off">Billed {billingFrequency()}, cancel anytime.</p>
                                </Grid>

                            </Grid>
                        </Box>
                    </Modal>





                </Grid>
                <div className={isTimerEnabled ? 'continueTimerOn' : 'continue'} style={isTimerEnabled ? { background: '#fff', padding: '10px' } : {}}>
                    <BottomHeader />
                    <Button
                        onClick={handleOpen}
                        sx={purchaseButton}
                        variant="contained"
                        color='primary'
                        size='large'
                        className=''
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </div >
    );
}

export default Card;